import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import CloseIcon from '@mui/icons-material/Close';
import WaterDropOutlinedIcon from '@mui/icons-material/WaterDropOutlined';
import AnimatedWeather from 'react-animated-weather';
import { alpha } from '@mui/material/styles';
import apiClient from '../services/api-client';

const WEATHER_LABELS = {
    CLEAR_DAY: 'Soleado',
    PARTLY_CLOUDY_DAY: 'Parcial',
    CLOUDY: 'Nublado',
    RAIN: 'Lluvia',
    SLEET: 'Aguanieve',
    SNOW: 'Nieve',
    FOG: 'Niebla',
    CLEAR_NIGHT: 'Despejado',
    PARTLY_CLOUDY_NIGHT: 'Parcial',
};

const ANIMATED_ICON_MAP = {
    CLEAR_DAY: 'CLEAR_DAY',
    CLEAR_NIGHT: 'CLEAR_NIGHT',
    CLOUDY: 'CLOUDY',
    FOG: 'FOG',
    PARTLY_CLOUDY_DAY: 'PARTLY_CLOUDY_DAY',
    PARTLY_CLOUDY_NIGHT: 'PARTLY_CLOUDY_NIGHT',
    RAIN: 'RAIN',
    SLEET: 'SLEET',
    SNOW: 'SNOW',
};

const getLabel = (icon) => {
    const key = String(icon || '').toUpperCase();
    return WEATHER_LABELS[key] || 'Clima';
};

const getAnimatedIcon = (icon) => {
    const key = String(icon || '').toUpperCase();
    return ANIMATED_ICON_MAP[key] || 'CLEAR_DAY';
};

export default function WeatherModal({ open, onClose }) {
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [data, setData] = React.useState(null);

    const fetchWeather = React.useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiClient.get('weather/current', {}, { action: 'fetch_weather_forecast' });
            setData(response.data.data);
        } catch (err) {
            console.error(err);
            setError('No se pudo cargar el clima. Intenta de nuevo.');
        } finally {
            setLoading(false);
        }
    }, []);

    React.useEffect(() => {
        if (open) {
            fetchWeather();
        } else {
            setData(null);
            setError(null);
            setLoading(false);
        }
    }, [open, fetchWeather]);

    const icon = data?.icon;
    const forecast = Array.isArray(data?.forecast) ? data.forecast : [];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="xs"
            PaperProps={{ sx: { borderRadius: '16px', maxHeight: '85vh' } }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pb: 1,
                }}
            >
                <Typography component="div" variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Clima
                </Typography>
                <IconButton aria-label="cerrar" onClick={onClose} size="small">
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ px: 2.5, pb: 2.5 }}>
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                        <CircularProgress size={36} />
                    </Box>
                )}

                {!loading && error && (
                    <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
                        <Typography color="text.secondary" sx={{ mb: 1 }}>
                            {error}
                        </Typography>
                        <Typography
                            component="button"
                            onClick={fetchWeather}
                            sx={{
                                color: 'secondary.main',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                fontWeight: 600,
                                p: 0,
                                '&:hover': { textDecoration: 'underline' },
                            }}
                        >
                            Reintentar
                        </Typography>
                    </Box>
                )}

                {!loading && !error && data && (
                    <React.Fragment>
                        {/* Header / current weather */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                textAlign: 'center',
                                py: 1.5,
                                borderRadius: 3,
                                background: (theme) =>
                                    `linear-gradient(180deg, ${alpha(theme.palette.secondary.main, 0.10)} 0%, ${alpha(
                                        theme.palette.background.paper,
                                        0
                                    )} 100%)`,
                            }}
                        >
                            <Box sx={{ height: 96, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <AnimatedWeather
                                    icon={getAnimatedIcon(icon)}
                                    color="#238048"
                                    size={88}
                                    animate={true}
                                />
                            </Box>
                            <Typography sx={{ fontSize: 48, fontWeight: 900, lineHeight: 1.1, mt: 0.5 }}>
                                {data.temp}°
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                {getLabel(icon)}
                            </Typography>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        {/* 24h forecast */}
                        <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                            Pronóstico
                        </Typography>
                        <Box
                            sx={{
                                mt: 1,
                                overflowX: 'auto',
                                overflowY: 'hidden',
                                pb: 1,
                                '&::-webkit-scrollbar': { height: 6 },
                                '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.2),
                                    borderRadius: 3,
                                },
                            }}
                        >
                            <Stack direction="row" spacing={1} sx={{ minWidth: 'max-content' }}>
                                {forecast.map((item, idx) => (
                                    <Box
                                        key={`${item.time}-${idx}`}
                                        sx={{
                                            width: 64,
                                            flexShrink: 0,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            textAlign: 'center',
                                            py: 1.25,
                                            px: 0.5,
                                            borderRadius: 2,
                                            background: (theme) => alpha(theme.palette.secondary.main, 0.06),
                                        }}
                                    >
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                            {item.time}
                                        </Typography>
                                        <Box sx={{ my: 0.5, height: 36, display: 'flex', alignItems: 'center' }}>
                                            <AnimatedWeather
                                                icon={getAnimatedIcon(item.icon)}
                                                color="#238048"
                                                size={32}
                                                animate={true}
                                            />
                                        </Box>
                                        <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                                            {item.temperature}°
                                        </Typography>
                                        {item.precipitation > 0 ? (
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                spacing={0.25}
                                                sx={{ mt: 0.25, color: 'info.main' }}
                                            >
                                                <WaterDropOutlinedIcon sx={{ fontSize: 12 }} />
                                                <Typography variant="caption" sx={{ fontWeight: 600, fontSize: 11 }}>
                                                    {item.precipitation}%
                                                </Typography>
                                            </Stack>
                                        ) : (
                                            <Box sx={{ height: 16, mt: 0.25 }} />
                                        )}
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                    </React.Fragment>
                )}
            </DialogContent>
        </Dialog>
    );
}
